import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sun, Moon, Bell, LogOut, Shield, User } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { useMarket } from "../lib/store";
import { useTick } from "../lib/socket";
import { usePortfolio } from "../lib/usePortfolio";
import { inr, pct, signed, tone, istDateTime, fmt } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Price } from "./Price";
import { SimBadge } from "./SimBadge";
import { Odometer, QuoteDecode, MarketPulse } from "./motion";
import { TgPill } from "./community/TgBits";

const INDICES = [["NIFTY 50", "NSE_INDEX|Nifty 50"], ["SENSEX", "BSE_INDEX|SENSEX"], ["BANK NIFTY", "NSE_INDEX|Nifty Bank"]];

function IndexChip({ name, k }) {
  const t = useTick(k);
  const nav = useNavigate();
  const [decoded, setDecoded] = useState(false);
  const has = t?.ltp != null;
  useEffect(() => { if (!has || decoded) return undefined; const id = setTimeout(() => setDecoded(true), 900); return () => clearTimeout(id); }, [has, decoded]);
  return (
    <button onClick={() => nav(`/instrument/${encodeURIComponent(k)}`)} data-testid={`index-${name.replace(/\s/g, "-")}`}
      className="flex items-baseline gap-2 whitespace-nowrap px-2.5 h-8 items-center rounded-md border border-transparent hover:border-border hover:bg-accent/40 transition-colors duration-200">
      <span className="text-[10.5px] font-semibold tracking-wide text-muted-foreground">{name}</span>
      <span className={`text-[12px] ${tone(t?.ch)}`}>{decoded ? <Price value={t?.ltp} prev={t?.prevLtp} arrow /> : <QuoteDecode text={fmt(t?.ltp)} runKey={has ? k : null} />}</span>
      <span className={`text-[10.5px] num ${tone(t?.ch)}`}>{signed(t?.ch)} ({pct(t?.chp)})</span>
    </button>
  );
}

function MarketPill() {
  const status = useMarket((s) => s.status);
  const h = status?.health;
  const color = h === "live" ? "bg-up" : h === "delayed" ? "bg-amber-500" : "bg-down";
  return (
    <div className="hidden lg:flex items-center gap-1.5 text-[10.5px] text-muted-foreground" data-testid="market-status">
      <span data-testid="feed-health-dot" title={`Market data: ${h || "…"}`} className={`h-1.5 w-1.5 rounded-full ${color} ${h === "live" ? "pulse-soft" : ""}`} />
      <span data-testid="feed-health-label" className="uppercase tracking-wide">{h || "…"}</span>
      <MarketPulse ex="NSE" />
      <span className="mx-1 text-border">|</span><MarketPulse ex="MCX" />
    </div>
  );
}

function Notifications() {
  const version = useMarket((s) => s.portfolioVersion);
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/notifications").then((r) => setItems(r.data)).catch(() => {}); }, [version]);
  const unread = items.filter((i) => !i.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) api.post("/notifications/read").then(() => setItems((x) => x.map((i) => ({ ...i, read: true })))); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative h-8 w-8 grid place-items-center rounded-md hover:bg-accent">
          <Bell className="h-4 w-4" />
          {unread > 0 && <span className="absolute top-1 right-1 h-3.5 min-w-3.5 px-0.5 rounded-full bg-down text-[8px] text-white grid place-items-center num">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-popover">
        <div className="px-3 py-2 eyebrow border-b border-border">Notifications</div>
        <div className="max-h-80 overflow-auto thin-scroll">
          {items.length === 0 && <div className="p-4 text-[12px] text-muted-foreground">No notifications yet.</div>}
          {items.map((n) => (
            <div key={n.id} className="px-3 py-2 border-b border-border/60 text-[12px]">
              <div className={`font-medium ${n.level === "error" ? "text-down" : n.level === "success" ? "text-up" : ""}`}>{n.title}</div>
              <div className="text-muted-foreground text-[11px]">{n.body}</div>
              <div className="text-[10px] text-muted-foreground num mt-0.5">{istDateTime(n.at)}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TopBar({ theme, toggleTheme }) {
  const { user, signOut } = useAuth();
  const setSearch = useMarket((s) => s.setSearch);
  const funds = usePortfolio("/funds", 8000);
  const nav = useNavigate();
  return (
    <header className="h-12 shrink-0 border-b border-border glass flex items-center gap-3 px-3 z-40 relative">
      <span className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="hidden md:flex min-w-0 shrink items-center gap-1 overflow-x-auto no-scrollbar px-2" data-testid="indices-ticker">
        {INDICES.map(([n, k]) => <IndexChip key={k} name={n} k={k} />)}
      </div>
      <div className="flex-1" />
      <SimBadge testid="topbar-sim-badge" className="hidden lg:inline-flex shrink-0" />
      <div className="flex-1" />
      <MarketPill />
      <button data-testid="topbar-search-button" onClick={() => setSearch(true)}
        className="hidden sm:flex shrink-0 whitespace-nowrap items-center gap-2 h-8 w-56 px-2.5 rounded-md bg-muted/60 border border-border text-muted-foreground text-[12px] hover:border-primary/50 transition-colors duration-150">
        <Search className="h-3.5 w-3.5" /> <span className="flex-1 text-left">Search instruments</span>
        <kbd className="text-[9.5px] font-mono border border-border rounded px-1">Ctrl K</kbd>
      </button>
      <button className="sm:hidden h-8 w-8 grid place-items-center rounded-md hover:bg-accent" onClick={() => setSearch(true)} data-testid="topbar-search-mobile"><Search className="h-4 w-4" /></button>
      <button onClick={() => nav("/positions")} className="hidden xl:block text-right leading-tight" data-testid="topbar-today-pnl">
        <div className="eyebrow whitespace-nowrap">Today's P&L</div>
        <div className={`text-[12px] font-semibold ${tone(funds ? funds.realised_pnl_today + funds.unrealised_mtm : 0)}`}><Odometer text={funds ? signed(funds.realised_pnl_today + funds.unrealised_mtm) : "—"} testid="topbar-today-pnl-value" /></div>
      </button>
      <button onClick={() => nav("/funds")} className="hidden lg:block text-right leading-tight" data-testid="topbar-funds-summary">
        <div className="eyebrow whitespace-nowrap">Available · <span data-testid="topbar-virtual-label" className="text-amber-600 dark:text-amber-400">Virtual</span></div>
        <div className="num text-[12px] font-semibold">{inr(funds?.available_margin)}</div>
      </button>
      <TgPill />
      <Notifications />
      <button data-testid="topbar-theme-toggle" onClick={toggleTheme} className="h-8 w-8 grid place-items-center rounded-md hover:bg-accent">
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="profile-menu-trigger" className="h-8 w-8 rounded-full bg-primary/15 text-primary font-semibold text-[12px] grid place-items-center">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60 bg-popover">
          <DropdownMenuLabel>
            <div className="text-[13px]">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground font-normal">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => nav("/profile")} data-testid="profile-page-link"><User className="h-4 w-4 mr-2" />Profile & settings</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="profile-logout" onClick={signOut}><LogOut className="h-4 w-4 mr-2" />Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
